import I18n from 'I18n';
import * as PropertyTypes from 'customer-data-objects/property/PropertyTypes';
import * as OperatorTypes from '../../ObjectSegOperatorTypes';
import * as OperationTypes from '../../OperationTypes';
import { EXCLUSIVE, INCLUSIVE } from '../../timeFilters/TimeOperationEndpointBehaviorTypes';
import { VALUE, VALUE_WITH_ZONE_SAME_LOCAL_CONVERSION } from '../../timeFilters/TimeOperationPropertyParserTypes';
import { INDEXED } from '../../timeFilters/TimePointTimeTypes';
const getProximalOffset = (direction, isInclusive) => {
  // eslint-disable-next-line default-case
  switch (direction) {
    case 'forward':
      if (isInclusive) {
        // eg: "Forward 7 days including today"
        // The proximalOffset will be used as the lowerBoundOffset
        //    and 0 means, "the start of the day today"
        // The proximalOffset will be added to the signed value to determine the upperBoundOffset
        //    In this example, the signed value is +7,
        //    so the upperBoundOffset is 7 (= 0 + 7)
        //    and 7 means, "the start of the day 7 days from now"
        return 0;
      } else {
        // eg: "Forward 7 days NOT including today"
        // The proximalOffset will be used as the lowerBoundOffset
        //    and 1 means, "the start of the day tomorrow"
        // The proximalOffset will be added to the signed value to determine the upperBoundOffset
        //    In this example, the signed value is +7,
        //    so the upperBoundOffset is 8 (= 1 + 7)
        //    and 8 means, "the start of the day 8 days from now"
        return 1;
      }
    case 'backward':
      if (isInclusive) {
        // eg: "Back 7 days including today"
        // The proximalOffset will be used as the upperBoundOffset
        //    and 1 means, "the start of the day tomorrow"
        // The proximalOffset will be added to the signed value to determine the lowerBoundOffset
        //    In this example, the signed value is -7,
        //    so the upperBoundOffset is -6 (= 1 - 7)
        //    and -6 means, "the start of the day 6 days ago"
        return 1;
      } else {
        // eg: "Back 7 days NOT including today"
        // The proximalOffset will be used as the upperBoundOffset
        //    and 0 means, "the start of the day today"
        // The proximalOffset will be added to the signed value to determine the lowerBoundOffset
        //    In this example, the signed value is -7,
        //    so the upperBoundOffset is -7 (= 0 - 7)
        //    and -7 means, "the start of the day 7 days ago"
        return 0;
      }
  }
};
const getIndexReference = (timeUnit, useFiscalYear) => {
  // eslint-disable-next-line default-case
  switch (timeUnit) {
    case 'DAY':
      return {
        referenceType: 'TODAY'
      };
    case 'WEEK':
      return {
        referenceType: 'WEEK',
        dayOfWeek: 'SUNDAY'
      };
    case 'MONTH':
      return {
        referenceType: 'MONTH',
        day: 1
      };
    case 'QUARTER':
      return {
        referenceType: useFiscalYear ? 'FISCAL_QUARTER' : 'QUARTER',
        month: 1,
        day: 1
      };
    case 'YEAR':
      return {
        referenceType: useFiscalYear ? 'FISCAL_YEAR' : 'YEAR',
        month: 1,
        day: 1
      };
  }
};
const formatOffset = (offsetVector, timeUnit) => {
  // Omit offset if the magnitude is 0
  //   to (maybe) reduce the network payload size, serialization/deserialization time, and persisted size
  if (offsetVector === 0) {
    return undefined;
  }
  // eslint-disable-next-line default-case
  switch (timeUnit) {
    case 'DAY':
      return {
        days: offsetVector
      };
    case 'WEEK':
      return {
        weeks: offsetVector
      };
    case 'MONTH':
      return {
        months: offsetVector
      };
    case 'QUARTER':
      return {
        quarters: offsetVector
      };
    case 'YEAR':
      return {
        years: offsetVector
      };
  }
};
const getBoundOffsets = (direction, isInclusive, value) => {
  // The `anchorOffsetVector` is the offset anchored on the current time
  //   whereas the `derivedOffsetVector` is derived from the anchor and the value.
  const anchorOffsetVector = getProximalOffset(direction, isInclusive);
  const valueVector = direction === 'backward' ? -1 * value : value;
  const derivedOffsetVector = valueVector + anchorOffsetVector;
  // For "forward" rolling date configs, the `anchorOffset` is before the `derivedOffset`
  //   whereas for "backward", the `achorOffset` is after the `derivedOffset`.
  const [lowerBoundOffsetVector, upperBoundOffsetVector] = direction === 'backward' ? [derivedOffsetVector, anchorOffsetVector] : [anchorOffsetVector, derivedOffsetVector];
  return {
    lowerBoundOffsetVector,
    upperBoundOffsetVector
  };
};
export const toDynamicDateRangeOperation = operator => {
  var _operator$field;
  const {
    direction,
    includeFutureDates,
    isInclusive,
    timeUnit,
    useFiscalYear,
    value
  } = operator.value;
  const {
    lowerBoundOffsetVector,
    upperBoundOffsetVector
  } = getBoundOffsets(direction, isInclusive, value);

  // We only need one indexReference for lower and upper bound (for now)
  const indexReference = getIndexReference(timeUnit, useFiscalYear);
  const zoneId = I18n.moment.portalTz().tz() || 'UTC';
  const lowerBoundOffset = formatOffset(lowerBoundOffsetVector, timeUnit);
  const lowerBoundTimePoint = Object.assign({
    timeType: INDEXED,
    indexReference
  }, typeof lowerBoundOffset === 'undefined' ? {} : {
    offset: lowerBoundOffset
  }, {
    zoneId
  });

  // `includeFutureDates` = `false` is used to distinguish
  //   the "*_SO_FAR" variation of a preset from the standard one.
  // In the "*_SO_FAR" case we ignore the computed upper bound
  //   and use "the start of tomorrow."
  // The data structure allows setting `includeFutureDates` = `false`
  //   in cases where "the start of tomorrow" would be _before_ the lower bound (eg: "NEXT_WEEK"),
  //   so we ignore the value if it would cause the upper bound to be before the lower bound.
  const excludeDaysAfterToday = !includeFutureDates && lowerBoundOffsetVector < 1;
  const upperBoundOffset = formatOffset(upperBoundOffsetVector, timeUnit);
  const upperBoundTimePoint = excludeDaysAfterToday ? {
    timeType: INDEXED,
    indexReference: {
      referenceType: 'TODAY'
    },
    offset: {
      days: 1
    },
    zoneId
  } : Object.assign({
    timeType: INDEXED,
    indexReference
  }, typeof upperBoundOffset === 'undefined' ? {} : {
    offset: upperBoundOffset
  }, {
    zoneId
  });
  const propertyParser = ((_operator$field = operator.field) === null || _operator$field === void 0 ? void 0 : _operator$field.type) === PropertyTypes.DATE ? VALUE_WITH_ZONE_SAME_LOCAL_CONVERSION : VALUE;
  return {
    propertyParser,
    propertyType: OperationTypes.RANGED_TIME,
    operator: OperatorTypes.IS_BETWEEN,
    lowerBoundTimePoint,
    lowerBoundEndpointBehavior: INCLUSIVE,
    upperBoundTimePoint,
    // `upperBoundEndpointBehavior` determines whether the filter is inclusive or exclusive
    //   of the _millisecond_ referenced by the `upperBoundTimePoint`,
    //   not the entire unit of time referenced (eg: day),
    //   so really only the EXCLUSIVE behavior is useful to us.
    upperBoundEndpointBehavior: EXCLUSIVE
  };
};